import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API, formatError } from '../App';
import { toast } from 'sonner';

// Retry helper for transient network errors (ERR_NETWORK_CHANGED etc.)
const axiosWithRetry = async (config, retries = 3, delay = 500) => {
  for (let attempt = 1; attempt <= retries; attempt++) {
    try {
      return await axios(config);
    } catch (err) {
      const isTransient = !err.response;
      if (isTransient && attempt < retries) {
        await new Promise(r => setTimeout(r, delay * attempt));
        continue;
      }
      throw err;
    }
  }
};

import {
  Users,
  Factory,
  ClipboardCheck,
  IndianRupee,
  ArrowUpRight,
  PlusCircle,
  Calendar,
  CheckCircle2,
  Info
} from 'lucide-react';

const CACHE_KEY = 'dashboard_stats_cache';

const emptyStats = {
  total_employees: 0,
  today_attendance: 0,
  month_payroll_generated: 0,
  total_payroll_amount: 0,
  total_rigs: 0,
  today_date: ''
};

const Dashboard = ({ companyName: propCompanyName, logoUrl: propLogoUrl }) => {
  // Load cached stats immediately — no blank screen while fetching
  const [stats, setStats] = useState(() => {
    try {
      const cached = sessionStorage.getItem(CACHE_KEY);
      return cached ? JSON.parse(cached) : emptyStats;
    } catch {
      return emptyStats;
    }
  });
  const hasCachedData = !!sessionStorage.getItem(CACHE_KEY);
  const [loading, setLoading] = useState(!hasCachedData); // Only show spinner if no cache

  // Use props but allow local fallback if needed
  const companyName = propCompanyName || '';
  const logoUrl = propLogoUrl || null;

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      // Retries up to 3x on transient network errors (ERR_NETWORK_CHANGED etc.)
      const response = await axiosWithRetry({ method: 'get', url: `${API}/reports/dashboard` });
      setStats(response.data);
      setLoading(false);
      // Cache fresh data for next render
      sessionStorage.setItem(CACHE_KEY, JSON.stringify(response.data));
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      // Only show error toast after all retries exhausted
      if (!sessionStorage.getItem(CACHE_KEY)) {
        toast.error(formatError(error));
      }
      setLoading(false);
    }
  };

  // LCP FIX: Removed early spinner return — card grid now renders IMMEDIATELY.
  // StatCard shows shimmer placeholders when loading=true, real data when loaded.
  const StatCard = ({ title, value, subtitle, icon: Icon, gradient, trend, skeleton }) => (
    <div
      data-testid={`stat-card-${title.toLowerCase().replace(/\s+/g, '-')}`}
      className="relative overflow-hidden group rounded-2xl p-6 text-white shadow-xl transition-all duration-300 hover:shadow-2xl hover:-translate-y-1"
      style={{ background: gradient }}
    >
      <div className="relative z-10 flex flex-col h-full justify-between">
        <div className="flex items-start justify-between">
          <div className="p-2 bg-white/20 rounded-lg backdrop-blur-md">
            <Icon size={24} className="text-white" />
          </div>
          {skeleton ? (
            <div className="w-14 h-6 bg-white/20 rounded-full animate-pulse" />
          ) : trend && (
            <div className="flex items-center text-xs font-medium bg-white/20 px-2 py-1 rounded-full backdrop-blur-md">
              <ArrowUpRight size={14} className="mr-1" />
              {trend}
            </div>
          )}
        </div>
        <div className="mt-4">
          <p className="text-sm font-medium opacity-80 uppercase tracking-wider">{title}</p>
          {skeleton ? (
            <>
              <div className="h-9 w-24 bg-white/25 rounded-lg mt-1 animate-pulse" />
              <div className="h-3 w-32 bg-white/15 rounded mt-2 animate-pulse" />
            </>
          ) : (
            <>
              <h3 className="text-3xl font-bold mt-1 tracking-tight">{value}</h3>
              {subtitle && <p className="text-xs opacity-70 mt-2 font-medium">{subtitle}</p>}
            </>
          )}
        </div>
      </div>
      <div className="absolute top-0 right-0 -mr-8 -mt-8 w-32 h-32 bg-white/10 rounded-full blur-2xl group-hover:bg-white/20 transition-colors"></div>
    </div>
  );

  const currentMonth = new Date().toLocaleString('default', { month: 'long', year: 'numeric' });

  return (
    <div className="space-y-8" data-testid="dashboard-container">
      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          {logoUrl && (
            <div className="w-16 h-16 bg-white rounded-2xl shadow-sm border border-gray-100 flex items-center justify-center overflow-hidden p-2">
              <img src={logoUrl} alt="Logo" width="64" height="64" className="max-w-full max-h-full object-contain" />
            </div>
          )}
          <div>
            <h2 className="text-4xl font-extrabold text-gray-900 tracking-tight">{companyName || 'Dashboard'}</h2>
            <p className="text-gray-500 mt-2 flex items-center font-medium">
              <Calendar size={16} className="mr-2 text-blue-500" />
              System overview for {currentMonth}
            </p>
          </div>
        </div>
        <div className="bg-white px-5 py-3 rounded-2xl shadow-sm border border-gray-100 flex items-center">
          <div className="mr-4">
            <p className="text-[10px] uppercase font-bold text-gray-400 tracking-widest">Today's Date</p>
            <p className="text-lg font-bold text-gray-800">{stats.today_date}</p>
          </div>
          <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center">
            <Calendar size={20} className="text-blue-600" />
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Employees"
          value={stats.total_employees.toLocaleString()}
          subtitle="Total Active Personnel"
          icon={Users}
          gradient="linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)"
          trend="+2 New"
          skeleton={loading}
        />

        <StatCard
          title="Rigs / Sites"
          value={stats.total_rigs.toLocaleString()}
          subtitle="Operating Across Locations"
          icon={Factory}
          gradient="linear-gradient(135deg, #0891b2 0%, #0d9488 100%)"
          skeleton={loading}
        />

        <StatCard
          title="Attendance"
          value={stats.today_attendance.toLocaleString()}
          subtitle={`Present today out of ${stats.total_employees}`}
          icon={ClipboardCheck}
          gradient="linear-gradient(135deg, #059669 0%, #10b981 100%)"
          trend={`${Math.round((stats.today_attendance / stats.total_employees) * 100)}%`}
          skeleton={loading}
        />

        <StatCard
          title="Payroll"
          value={`₹${stats.total_payroll_amount.toLocaleString('en-IN')}`}
          subtitle={`Total Payout for ${new Date().toLocaleString('default', { month: 'short' })}`}
          icon={IndianRupee}
          gradient="linear-gradient(135deg, #ea580c 0%, #f97316 100%)"
          skeleton={loading}
        />
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Quick Actions */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-3xl p-8 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between mb-8">
              <h3 className="text-2xl font-bold text-gray-900">Quick Actions</h3>
              <PlusCircle size={24} className="text-gray-300" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <a
                href="/rigs"
                data-testid="quick-action-manage-rigs"
                className="group p-5 bg-slate-50 hover:bg-blue-600 rounded-2xl transition-all duration-300 flex items-start"
              >
                <div className="w-12 h-12 bg-white rounded-xl shadow-sm flex items-center justify-center mr-4 group-hover:scale-110 transition-transform">
                  <Factory size={22} className="text-blue-600" />
                </div>
                <div>
                  <p className="font-bold text-gray-900 group-hover:text-white transition-colors">Manage Rigs</p>
                  <p className="text-sm text-gray-500 group-hover:text-blue-100 transition-colors mt-1">Configure work sites</p>
                </div>
              </a>

              <a
                href="/employees"
                data-testid="quick-action-add-employee"
                className="group p-5 bg-slate-50 hover:bg-indigo-600 rounded-2xl transition-all duration-300 flex items-start"
              >
                <div className="w-12 h-12 bg-white rounded-xl shadow-sm flex items-center justify-center mr-4 group-hover:scale-110 transition-transform">
                  <Users size={22} className="text-indigo-600" />
                </div>
                <div>
                  <p className="font-bold text-gray-900 group-hover:text-white transition-colors">Staff Registry</p>
                  <p className="text-sm text-gray-500 group-hover:text-indigo-100 transition-colors mt-1">Manage personnel</p>
                </div>
              </a>

              <a
                href="/attendance"
                data-testid="quick-action-mark-attendance"
                className="group p-5 bg-slate-50 hover:bg-emerald-600 rounded-2xl transition-all duration-300 flex items-start"
              >
                <div className="w-12 h-12 bg-white rounded-xl shadow-sm flex items-center justify-center mr-4 group-hover:scale-110 transition-transform">
                  <ClipboardCheck size={22} className="text-emerald-600" />
                </div>
                <div>
                  <p className="font-bold text-gray-900 group-hover:text-white transition-colors">Mark Log</p>
                  <p className="text-sm text-gray-500 group-hover:text-emerald-100 transition-colors mt-1">Track daily presence</p>
                </div>
              </a>

              <a
                href="/payroll"
                data-testid="quick-action-generate-payroll"
                className="group p-5 bg-slate-50 hover:bg-orange-600 rounded-2xl transition-all duration-300 flex items-start"
              >
                <div className="w-12 h-12 bg-white rounded-xl shadow-sm flex items-center justify-center mr-4 group-hover:scale-110 transition-transform">
                  <IndianRupee size={22} className="text-orange-600" />
                </div>
                <div>
                  <p className="font-bold text-gray-900 group-hover:text-white transition-colors">Payroll Engine</p>
                  <p className="text-sm text-gray-500 group-hover:text-orange-100 transition-colors mt-1">Process monthly wages</p>
                </div>
              </a>
            </div>
          </div>
        </div>

        {/* System Info Sidebar */}
        <div className="space-y-8">
          <div className="bg-white rounded-3xl p-8 shadow-sm border border-gray-100">
            <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
              <Info size={20} className="mr-2 text-blue-500" />
              Information
            </h3>
            <div className="space-y-6">
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-2xl border border-gray-100">
                <span className="text-sm font-semibold text-gray-500 uppercase tracking-wider">Payment Models</span>
                <span className="text-2xl font-bold text-blue-600">3</span>
              </div>

              <div className="space-y-3">
                {[
                  { label: "Minimum Wage", color: "bg-blue-500" },
                  { label: "Fair Wage", color: "bg-emerald-500" },
                  { label: "Monthly Salary", color: "bg-purple-500" }
                ].map((mt, i) => (
                  <div key={i} className="flex items-center p-3 hover:bg-gray-50 rounded-xl transition-colors">
                    <div className={`w-2 h-2 ${mt.color} rounded-full mr-3 shadow-sm`}></div>
                    <span className="text-sm font-bold text-gray-700">{mt.label}</span>
                  </div>
                ))}
              </div>

              <div className="p-4 bg-blue-50 rounded-2xl border border-blue-100 mt-4">
                <div className="flex items-start">
                  <div className="p-1.5 bg-blue-100 rounded-lg mr-3 text-blue-600">
                    <CheckCircle2 size={16} />
                  </div>
                  <p className="text-xs text-blue-800 leading-relaxed font-medium">
                    All payroll generated via this portal follows statutory guidelines and company policies.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
